import {
  ArrowRight,
  BarChart2,
  ChevronRight,
  Crosshair,
  Plus,
  Save,
  Server,
  Users2,
  Video,
  Wrench,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import React from 'react';
import { Link } from 'react-router';

import replay2d from '@/assets/2d-replay.jpg';
import enviroment from '@/assets/enviroment-graphic.svg';
import FaceitBig from '@/assets/faceitbig.png';
import grenadeFinder from '@/assets/grenade-finder.jpg';
import trajectoryHelper from '@/assets/helper.jpg';
import impact from '@/assets/impact.png';
import logoBg from '@/assets/logo-bg.png';
import multiGame from '@/assets/multi.png';
import pattern from '@/assets/pattern.png';
import livefeedback from '@/assets/plugins.jpg';
import botReplay from '@/assets/practice_preview.jpg';
import recreateScenario from '@/assets/recreate-scenario.jpg';
import serverModes from '@/assets/server-modes.jpg';
import { MainLayout } from '@/components/layouts';
import Footer from '@/components/layouts/footer';
import { Head } from '@/components/seo';
import { Button } from '@/components/ui/button';
import { Compare7 } from '@/components/ui/compare';
import DemoSection from '@/features/landing/components/demo-section';
import FeatureItem from '@/features/landing/components/feature-item';
import SCLPackage from '@/features/landing/components/scl-package';
import ServerLocations from '@/features/landing/components/server-locations';
import { useDefaultTeam } from '@/features/users/api/getDefaultTeam';
import { useSession } from '@/hooks/useSession';

interface CoreFeature {
  key: string;
  icon: React.ReactNode;
  label: string;
}

const coreFeatures: CoreFeature[] = [
  {
    key: 'server',
    icon: <Server size={16} strokeWidth={1} />,
    label: '24/7 Server (28 locations)',
  },
  {
    key: 'scrim',
    icon: <Crosshair size={16} strokeWidth={1} />,
    label: 'Scrim, Practice, and Retake modes',
  },
  {
    key: 'replay',
    icon: <Video size={16} strokeWidth={1} />,
    label: '2D Replay (Advanced)',
  },
  {
    key: 'demoStorage',
    icon: <Save size={16} strokeWidth={1} />,
    label: 'Demo storage',
  },
  {
    key: 'analytics',
    icon: <BarChart2 size={16} strokeWidth={1} />,
    label: 'Match analytics',
  },
  {
    key: 'serverSlots',
    icon: <Users2 size={16} strokeWidth={1} />,
    label: '14 Server slots',
  },
];

const LandingRoute: React.FC = () => {
  const { isLoggedIn, isLoading: isSessionLoading } = useSession();

  const { data: defaultTeam, isLoading: isLoadingDefault } = useDefaultTeam({
    queryConfig: {
      enabled: isLoggedIn,
    },
  });

  const isLoading = isSessionLoading || (isLoggedIn && isLoadingDefault);

  const isPremiumTeam = defaultTeam?.isPremium;

  const buttonConfig = isLoading
    ? { to: '#', text: '', icon: null, disabled: true }
    : isLoggedIn
    ? {
        to: isPremiumTeam ? '/server' : '/onboarding',
        text: isPremiumTeam ? 'Go to Server' : 'Get Started',
        icon: <ChevronRight size={16} />,
        disabled: false,
      }
    : {
        to: '/register',
        text: 'Get started now',
        icon: <Plus size={16} />,
        disabled: false,
      };
  return (
    <MainLayout>
      <Head
        title="Practice, Servers & 2D Replay"
        description="Join SCL, the leading team-oriented practice platform, designed to accelerate team and player improvement faster than any other third-party site."
      />
      <img
        src={logoBg}
        alt="Logo BG"
        className="absolute top-0 -z-10 h-auto min-h-[800px] w-full object-cover"
      />
      <section className="sm:py-4 sm:pb-20 md:max-w-6xl lg:pt-64 mx-auto max-w-5xl px-6 pt-32">
        <div className="xl:gap-16 flex flex-col justify-between gap-12">
          <div className="lg:items-start lg:text-left flex w-full shrink-0 flex-col items-center text-center">
            <p className="sm:text-base font-codeSaver text-sm font-medium uppercase tracking-wider text-muted-foreground">
              The complete team platform
            </p>
            <h1 className="lg:text-4xl mt-2 text-pretty text-4xl font-semibold !leading-snug">
              <span className="text-primary">CS2</span> Server Hosting & 2D
              Replay<br></br>- For Teams
            </h1>
          </div>
          <div className="lg:flex-row lg:justify-between mt-[-45px] flex flex-col items-center gap-12">
            <div className="sm:grid-cols-2 grid gap-9">
              {coreFeatures.map((feature) => (
                <FeatureItem
                  key={feature.key}
                  icon={feature.icon}
                  label={feature.label}
                />
              ))}
            </div>
            <div className="sm:w-auto flex w-full flex-col">
              <div className="space-y-1.5 rounded-t-lg border px-12 py-6">
                <div className="flex items-center justify-center gap-3 text-4xl font-semibold">
                  €19.90{' '}
                  <span className="text-xl text-muted-foreground">/ month</span>
                </div>
                <div className="flex items-center justify-center gap-1 text-xs font-medium tracking-tight text-muted-foreground">
                  Billied Yearly €24.90 billied monthly
                </div>
              </div>
              <div className="flex items-center justify-center gap-1 rounded-b-lg border-x border-b py-2 text-xs font-medium text-muted-foreground">
                <Users2 className="text-primary" size={14} strokeWidth={1} />{' '}
                Full team access - one subscription only.
              </div>
              <Link to={buttonConfig.to}>
                <Button
                  className="mt-4 w-full bg-gradient-to-br from-primary to-[#0A368E] uppercase hover:to-[#041b49]"
                  disabled={buttonConfig.disabled}
                >
                  <AnimatePresence mode="wait">
                    {buttonConfig.text && (
                      <motion.span
                        key={buttonConfig.text}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center"
                      >
                        {buttonConfig.text}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </Link>
              <p className="mt-1.5 text-xs font-semibold text-muted-foreground">
                We <span className="italic">sometimes</span> offer small
                organization discounts, apply{' '}
                <a
                  className="text-primary underline"
                  target="_blank"
                  rel="noreferrer"
                  href="https://docs.google.com/forms/d/e/1FAIpQLSdyI0gpTU_ZDEleHQyIIa_6CGearNh1CQHkwZS1ZuQsQP6MDw/viewform?usp=dialog"
                >
                  here.
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Compare7></Compare7>

      <section className="sm:py-24 md:max-w-7xl mx-auto max-w-5xl px-6 py-8 pb-12 pt-20">
        <div className="mx-auto max-w-max rounded-3xl bg-gradient-to-br from-primary to-[#0A368E] p-px">
          <div className="rounded-[calc(1.5rem-1px)] bg-background px-6 py-2.5 text-sm">
            Our core features
          </div>
        </div>
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          videoSrc="https://www.youtube.com/embed/GnYBTZBDkK8?si=nukQKXyixKrb-of7&autoplay=1"
          image={recreateScenario}
          title="Recreate Scenario"
          description="This feature allows you to use a 2D Replay to recreate an ingame scenario with bots - that means you can truly immerse yourself in your post-game insights."
          ctaText="Watch demo"
        />
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          videoSrc="https://www.youtube.com/embed/FNy9Kcp1zZc?si=lNqUzlkZuuV7VbXv&autoplay=1"
          image={multiGame}
          title="2D Multi Game"
          description="Multi Game allows you to play multiple matches simultaneously to more easily detect patterns in teams. This is super useful for match-preperation and making notes."
          ctaText="Watch demo"
        />
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          videoSrc="https://www.youtube.com/embed/ZU3o5-6UBtY?si=m_OH1xt4LaF4DQXF&autoplay=1"
          image={FaceitBig}
          title="FACEIT & ESEA Matches"
          description="At SCL you won't have to spend time finding the demos you want to review, simply search for the team you want to review and we index all teams from all ESEA divisions."
          ctaText="Watch demo"
        />
      </section>

      <section className="sm:py-12 sm:pb-24 md:max-w-6xl mx-auto max-w-5xl px-6 py-8 pb-12">
        <DemoSection
          videoSrc="https://www.youtube.com/embed/aD6bXRPgxto?si=mZfXXckaGJIckasT&autoplay=1"
          image={serverModes}
          title="Server Modes"
          description="Scrim, retake & practice - we offer all server modes on SCL."
          ctaText="Watch demo"
        />
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          videoSrc="https://www.youtube.com/embed/VDU4DKkS868?si=KomVd1E8z7EtL-Wc&autoplay=1"
          image={pattern}
          title="Pattern Finder"
          description="Evaluate patterns closer and get nerdy about how other teams play, and how you can beat them. State of the art technology."
          ctaText="Watch demo"
        />
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          videoSrc="https://www.youtube.com/embed/6hlMpcwsdNM?si=yeZwEto83TsvQnjz&autoplay=1"
          image={botReplay}
          title="Bot Replay"
          description="Set up strategies using bots. A brilliant tool for coaches that are looking to show how they wish their team to play."
          ctaText="Watch demo"
        />
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          videoSrc="https://www.youtube.com/embed/WVEk4hZce-s?si=euJSNXHsLtG5_3s2&autoplay=1"
          image={replay2d}
          title="Advanced 2D Viewer"
          description="Analyze every detail of your matches with our advanced 2D viewer, offering precise insights into positioning, timings, and strategies."
          ctaText="Watch demo"
        />
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          image={livefeedback}
          title="Live Feedback"
          description="Get feedback once every round is completed about what went wrong, instead of having to review the mistakes afterwards. This option can be disabled."
          ctaText="Watch demo"
        />
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          videoSrc="https://www.youtube.com/embed/1kmfZIY3cOc?si=14Ak7z83V4cEeaT3&autoplay=1"
          image={trajectoryHelper}
          title="Trajectory Helper"
          description="Use the .preview command to find Jumpthrows and W Jumpthrows more easily. No more testing needed, you get instant feedback in the trajectory camera!"
          ctaText="Watch demo"
        />
      </section>

      <section className="sm:py-24 sm:pb-12 md:max-w-7xl mx-auto max-w-5xl px-6 py-8 pt-20">
        <div className="mx-auto max-w-max rounded-3xl bg-gradient-to-br from-primary to-[#0A368E] p-px">
          <div className="flex items-center gap-x-2 rounded-[calc(1.5rem-1px)] bg-background px-6 py-2.5 text-sm">
            <Wrench size={16} className="text-primary" />
            Future features
          </div>
        </div>
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          image={grenadeFinder}
          title="Grenade Finder"
          description="Discover and perfect your grenade lineups with an intuitive tool designed to enhance your tactical preparation and execution."
          ctaText="Watch demo"
          showBadge
          badgeLabel="Coming Soon"
        />
      </section>

      <section className="sm:py-12 md:max-w-6xl mx-auto max-w-5xl px-6 py-8">
        <DemoSection
          title="IMPACT Score"
          image={impact}
          description="The IMPACT Score will allow you to identify the places on the map where you on average have the highest impact per round."
          ctaText="Watch demo"
          showBadge
          badgeLabel="Coming soon"
        />
      </section>

      <ServerLocations />

      <SCLPackage />

      <section className="sm:pt-24 md:max-w-screen-2xl mx-auto max-w-5xl px-6 pb-64 pt-12">
        <div className="sm:flex sm:p-14 relative block rounded-md border p-8">
          <div className="flex max-w-3xl flex-col">
            <h2 className="sm:text-4xl text-2xl font-semibold leading-relaxed">
              Servers, demos, 2D replays and scrim statistics - all in{' '}
              <span className="text-primary">one place</span>
            </h2>
            <div className="sm:flex-row mt-10 flex flex-col gap-x-4">
              <>
                {isLoggedIn ? (
                  <Link to={isPremiumTeam ? '/server' : '/onboarding'}>
                    <Button className="sm:mb-0 sm:w-auto mb-2 w-full">
                      <ChevronRight size={16} />{' '}
                      {isPremiumTeam ? 'Go to Server' : 'Get Started'}
                    </Button>
                  </Link>
                ) : (
                  <Link to="/register">
                    <Button className="sm:mb-0 sm:w-auto mb-2 w-full">
                      <Plus size={16} /> Get started now
                    </Button>
                  </Link>
                )}
              </>
              <Link to="/demo-viewer">
                <Button variant="outline" className="sm:w-auto w-full">
                  <ArrowRight size={16} /> Try the 2D viewer now
                </Button>
              </Link>
            </div>
            <img
              src={enviroment}
              alt="enviroment"
              className="xl:block absolute right-0 top-0 -z-10 hidden h-full"
            />
          </div>
        </div>
      </section>
      <Footer />
    </MainLayout>
  );
};

export default LandingRoute;
